/**
 * SplitAuthLayout — two-column auth layout (Variation A · Editorial Split).
 *
 * Left BrandPanel: paper-2 surface, paper-grid overlay, CoreLogo top, big
 * Fraunces editorial headline, body paragraph, trust pill + 2026 © credential.
 * Right FormShell: warm-bg surface, centered 440px column with form content
 * supplied via children. Brand panel hidden below md; mobile shows logo above.
 */

import type { ReactNode } from 'react';
import { CoreLogo } from '@/components/brand/CoreLogo';

interface SplitAuthLayoutProps {
  children: ReactNode;
}

export function SplitAuthLayout({ children }: SplitAuthLayoutProps) {
  return (
    <div className="flex min-h-screen bg-warm-bg text-ink">
      {/* — BrandPanel (left) — */}
      <aside className="relative hidden overflow-hidden border-r border-warm-border bg-paper-2 md:flex md:w-[42%]">
        {/* subtle horizontal paper grid */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-35 [background-image:linear-gradient(var(--color-warm-border)_1px,transparent_1px)] [background-size:100%_28px] [mask-image:linear-gradient(180deg,transparent_0%,#000_18%,#000_82%,transparent_100%)] [-webkit-mask-image:linear-gradient(180deg,transparent_0%,#000_18%,#000_82%,transparent_100%)]"
        />

        <div className="relative z-10 flex min-h-screen w-full flex-col justify-between px-11 py-10 lg:px-12">
          {/* top: logo */}
          <CoreLogo size="sm" />

          {/* middle: headline + body */}
          <div className="flex flex-col gap-[22px]">
            <h1 className="m-0 font-display text-[42px] font-normal leading-[1.02] tracking-[-0.02em] text-ink lg:text-[56px]">
              SEO content
              <br />
              that{' '}
              <em className="font-normal italic text-coral-dark">compounds.</em>
            </h1>
            <p className="m-0 max-w-[380px] text-[15.5px] leading-[1.55] text-ink-2">
              Plan, publish, and measure search work from one focused Core
              workspace.
            </p>
          </div>

          {/* bottom: trust pill + credit */}
          <div className="flex items-center justify-between">
            <span className="inline-flex items-center gap-2 rounded-full border border-warm-border bg-paper-2 px-3 py-[6px] font-mono text-[11px] tracking-[0.06em] text-ink-2">
              <span className="size-[6px] rounded-full bg-teal-dark shadow-[0_0_0_3px_var(--color-teal-light)]" />
              Content intelligence workspace
            </span>
            <span className="font-mono text-[11px] tracking-[0.1em] text-ink-3">
              2026 ©
            </span>
          </div>
        </div>
      </aside>

      {/* — FormShell (right) — */}
      <main className="relative flex w-full items-center justify-center bg-warm-bg px-6 py-14 sm:px-12 md:w-[58%] md:px-16 md:py-14">
        <div className="flex w-full max-w-[440px] flex-col gap-7">
          {/* mobile-only logo above the form */}
          <div className="md:hidden">
            <CoreLogo size="sm" />
          </div>
          {children}
        </div>
      </main>
    </div>
  );
}
